// The single source for every colour list the app uses in JavaScript:
// the accent picker, client colour swatches, automatic client colours and the
// chart palette. styles/tokens.css mirrors ACCENTS (the --brand-600 shade of
// each data-accent palette) and CHART_COLORS (--chart-1 … --chart-8); keep
// both in sync when you change a value here.
//
// In components, prefer the CSS variables (chartColor(i) → 'var(--chart-n)')
// so the colours follow the theme. Use the hex values only where CSS variables
// do not work (PDF/CSV exports, canvas, <input type="color">).
import type { Accent } from '../prefs.svelte';

/** Timepeak brand blue (#2563eb), for non-CSS contexts such as PDF exports. */
export const BRAND_HEX = '#2563eb';

export interface AccentOption {
  value: Accent;
  /** The 600 shade of the accent palette in tokens.css. */
  color: string;
}

/** Accent preference options, in picker order. */
export const ACCENTS: AccentOption[] = [
  { value: 'blue', color: '#2563eb' },
  { value: 'indigo', color: '#4f46e5' },
  { value: 'violet', color: '#7c3aed' },
  { value: 'sky', color: '#0284c7' },
  { value: 'teal', color: '#0d9488' },
  { value: 'green', color: '#16a34a' },
  { value: 'orange', color: '#ea580c' },
  { value: 'rose', color: '#e11d48' },
  { value: 'slate', color: '#334155' }
];

/** Colours a user can pick for a client (ColorSwatches), in picker order. */
export const CLIENT_COLORS: string[] = [
  '#2563eb', '#0ea5e9', '#06b6d4', '#14b8a6', '#10b981', '#84cc16', '#f59e0b',
  '#f97316', '#ef4444', '#ec4899', '#8b5cf6', '#6366f1', '#64748b'
];

/**
 * Colours given automatically to clients without their own colour, by id.
 * A subset of CLIENT_COLORS; the order is fixed so existing clients keep
 * their colour.
 */
export const CLIENT_AUTO_COLORS: string[] = [
  '#2563eb', '#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#f97316', '#14b8a6', '#6366f1', '#84cc16'
];

/** Hex values of --chart-1 … --chart-8 (light theme, blue accent). */
export const CHART_COLORS: string[] = ['#2563eb', '#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#64748b', '#f97316'];

/** The CSS variable for categorical series i (wraps after 8). */
export function chartColor(index: number): string {
  return `var(--chart-${(Math.abs(index) % CHART_COLORS.length) + 1})`;
}

/** The hex value for categorical series i (exports, canvas). */
export function chartHex(index: number): string {
  return CHART_COLORS[Math.abs(index) % CHART_COLORS.length];
}

/** True for a usable #rgb / #rrggbb / #rrggbbaa colour. */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{3,8}$/i.test(value);
}

/** Automatic client colour for an id (stable). */
export function autoClientColor(id: number | null | undefined): string {
  const n = Number(id) || 0;
  return CLIENT_AUTO_COLORS[Math.abs(n) % CLIENT_AUTO_COLORS.length];
}
